import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../contexts/AuthContext';
import { useWalletOptional } from '../../../../contexts/WalletContext';
import { useSelectedChain, CHAIN_NAMES } from '../../../../contexts/SelectedChainContext';
import LanguageSwitcher from '../../../../shared/components/LanguageSwitcher/LanguageSwitcher';
import './Header.css';

const Header: React.FC = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const wallet = useWalletOptional();
  const { selectedChainId } = useSelectedChain();
  const isWrongForSelected = selectedChainId != null && wallet?.isWrongChainFor(selectedChainId);
  const chainLabel = wallet?.chainId != null
    ? (isWrongForSelected ? 'Wrong network' : (CHAIN_NAMES[wallet.chainId] ?? `Chain ${wallet.chainId}`))
    : null;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsDropdownOpen(false);
  };

  const getInitials = (username: string) => {
    return username?.charAt(0).toUpperCase() || 'U';
  };

  return (
    <header className="dashboard-header">
      <div className="header-content">
        <div className="header-right">
          {wallet && (
            <div className="header-wallet-section">
              {wallet.account ? (
                <>
                  <span className="header-wallet-address" title={wallet.account}>
                    {wallet.truncateAddress(wallet.account)}
                  </span>
                  {chainLabel && (
                    <span className={`header-network-badge ${isWrongForSelected ? 'wrong' : ''}`}>
                      {chainLabel}
                    </span>
                  )}
                  <button
                    type="button"
                    className="header-disconnect-btn"
                    onClick={wallet.disconnect}
                    aria-label={t('common.disconnect')}
                  >
                    {t('common.disconnect')}
                  </button>
                </>
              ) : (
                <Link to="/dashboard/trade" className="header-connect-wallet">
                  <i className="fas fa-wallet"></i>
                  <span>{t('common.connectWallet')}</span>
                </Link>
              )}
            </div>
          )}
          <LanguageSwitcher variant="dashboard" />
          <div className="user-menu-wrapper" ref={dropdownRef}>
            <button 
              className="user-menu-trigger"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              aria-label={t('header.userMenu')}
            >
              <div className="user-avatar-header">
                {user?.profile_picture_url ? (
                  <img 
                    src={user.profile_picture_url} 
                    alt={user?.username || 'User'} 
                    className="user-avatar-img-header" 
                  />
                ) : (
                  <span className="user-initials">{getInitials(user?.username || '')}</span>
                )}
              </div>
              <i className={`fas fa-chevron-down dropdown-arrow ${isDropdownOpen ? 'open' : ''}`}></i>
            </button>

            {isDropdownOpen && (
              <div className="user-dropdown-menu">
                <div className="user-dropdown-header">
                  <div className="user-avatar-dropdown">
                    {user?.profile_picture_url ? (
                      <img 
                        src={user.profile_picture_url} 
                        alt={user?.username || 'User'} 
                        className="user-avatar-img-dropdown" 
                      />
                    ) : (
                      <span className="user-initials-dropdown">{getInitials(user?.username || '')}</span>
                    )}
                  </div>
                  <div className="user-info-dropdown">
                    <p className="user-name-dropdown">{user?.username || 'User'}</p>
                    <p className="user-email-dropdown">{user?.email || ''}</p>
                  </div>
                </div>

                <div className="dropdown-divider"></div>

                <div className="dropdown-actions">
                  <Link 
                    to="/dashboard/settings" 
                    className="dropdown-action-item"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    <i className="fas fa-cog"></i>
                    <span>{t('common.settings')}</span>
                  </Link>
                  
                  <button 
                    onClick={handleLogout} 
                    className="dropdown-action-item logout-action"
                  >
                    <i className="fas fa-sign-out-alt"></i>
                    <span>{t('nav.logout')}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;

